import { FC, Fragment } from 'react'
import { CSSContainerQuery, Menu, TopBarNavIconItemButton } from '@reapit/elements'
import { MenuIcon } from '@reapit/elements/icons/menu'
import { NavigationMenu, SecondaryNavigationMenu, UserNavigationMenu } from '../types'
import { SubMenu } from './SubMenu'
import { elMobileMenuOverflowYScroll } from '../styles'
import { MainMenuMobileItem } from './MainMenuMobileItem'

interface MainMenuMobileProps {
  menu: NavigationMenu[]
  secondaryMenu: SecondaryNavigationMenu[]
  userMenu: UserNavigationMenu
}

/**
 * `MainMenuMobile` is a component that renders a list of navigation items.
 * Each item is clickable and executes the corresponding onClick when clicked.
 *
 * Props:
 * @param menu (NavigationMenu[]): An array of navigation menu items,
 * each containing an `itemIndex`, `title`, and `onClick`.
 *
 * This component:
 * 1. Iterates through the `menu` array and renders each `NavIconItem`.
 * 2. Each `NavIconItem` is clickable and triggers its respective `onClick` when clicked.
 */
export const MainMenuMobile: FC<MainMenuMobileProps> = ({ menu, secondaryMenu, userMenu }) => {
  const filteredMenu = menu.filter((navMenu) => navMenu.hasAccess !== false)
  const filteredSecondaryMenu = secondaryMenu.filter((navMenu) => navMenu.hasAccess !== false)
  const filteredUserMenu = userMenu.subMenu.filter((navMenu) => navMenu.hasAccess !== false)

  // Split into groups based on whether they have submenus
  const secondaryWithoutSubMenus = filteredSecondaryMenu.filter((item) => !item.subMenu?.length)
  const secondaryWithSubMenus = filteredSecondaryMenu.filter((item) => item.subMenu?.length)

  return (
    <>
      <TopBarNavIconItemButton
        aria-label="mobile menu"
        icon={<MenuIcon />}
        {...Menu.getTriggerProps({
          id: 'mobile-menu-icon',
          popoverTarget: 'mobile-menu',
          popoverTargetAction: 'toggle',
        })}
      />
      <Menu
        placement="bottom-end"
        aria-labelledby="mobile-menu-icon"
        id="mobile-menu"
        className={elMobileMenuOverflowYScroll}
      >
        {/* Main navigation */}
        <Menu.Group>
          {filteredMenu.map((navMenu) => (
            <MainMenuMobileItem key={navMenu.itemIndex} {...navMenu} />
          ))}
        </Menu.Group>

        {/* Secondary navigation items without submenus */}
        {secondaryWithoutSubMenus.length > 0 && <Menu.Divider />}
        <Menu.Group>
          {secondaryWithoutSubMenus.map(({ label, href, onClick, subMenu }, i) => {
            if (!subMenu?.length) {
              return (
                <MainMenuMobileItem key={label} {...{ href: href as string, title: label, onClick, itemIndex: i }} />
              )
            }
          })}
        </Menu.Group>

        {/* Secondary navigation with submenus */}
        {secondaryWithSubMenus.map((navMenu) => {
          const filteredSubMenu = navMenu.subMenu?.filter((navMenu) => navMenu.hasAccess !== false)
          return filteredSubMenu?.map((subMenu, index, arr) => {
            return (
              <Fragment key={subMenu.label}>
                {index === 0 && <Menu.Divider />}
                <SubMenu {...subMenu} key={subMenu.label} />
                {index < arr.length - 1 && subMenu.displayAsGroup !== false && <Menu.Divider />}
              </Fragment>
            )
          })
        })}

        {/* User menu (only visible on desktop width and above) */}
        <CSSContainerQuery condition={'not (width < 1024px)'}>
          {filteredUserMenu.length > 0 && <Menu.Divider />}
          {filteredUserMenu.map((navMenu, index, arr) => (
            <Fragment key={navMenu.label}>
              <SubMenu {...navMenu} key={navMenu.label} />
              {index < arr.length - 1 && <Menu.Divider />}
            </Fragment>
          ))}
        </CSSContainerQuery>
      </Menu>
    </>
  )
}
